import Loading from "../../components/loading/loading";
import {initAccordionComponents} from "../../components/base-accordion/accordion";

class StatisticsHandler {
    public static readonly Options = {
        selectors: {
            resultContainer: '.dws__account-statistics-container',
            loading: '.js-loading__account-statistics',
            productListContainer: '.dws__m15__product-list__results',
            productListAccordionContainer: '.dws__m54-accordion',
            paginationLinks: '.dws__m19-pagination__list-item a',
            form: '.dws__account-statistics-form',
            formResetLink: '.dws__m17-filter__reset-btn',
        },
        attributes: {
            apiEndpoint: 'data-statistics-endpoint',
        },
        supportedFilters: ['person', 'productType[]', 'progressStatus[]']
    }
    private readonly statisticsEndpoint: string | null;
    private readonly resultContainer: HTMLElement | null;
    private page: number | undefined;
    private searchParams: Array<any> | undefined;
    private lastUsedUrl: URL | null | undefined;

    constructor(element: HTMLElement) {
        this.statisticsEndpoint = element.getAttribute(StatisticsHandler.Options.attributes.apiEndpoint) ?? null;
        this.resultContainer = element.querySelector(StatisticsHandler.Options.selectors.resultContainer) ?? null;
        if (!(this.resultContainer instanceof HTMLElement)) {
            return;
        }
        this.initComponent();
    }

    private async initComponent() {
        const loading = this.initLoading(StatisticsHandler.Options.selectors.loading);

        try {
            await this.getStatisticsResponse();
        } finally {
            loading?.destroy();
        }
    }

    private async updateStatistics() {
        await this.getStatisticsResponse(true);
    }

    private async getStatisticsResponse(isUpdate: boolean = false): Promise<void> {
        if (!this.statisticsEndpoint || !this.resultContainer) return;
        let loading = null
        let endpoint = this.statisticsEndpoint
        if (isUpdate) {
            const productListContainerLoadingSelector = StatisticsHandler.Options.selectors.productListContainer + ' ' + StatisticsHandler.Options.selectors.loading;
            loading = this.initLoading(productListContainerLoadingSelector);
            const productListContainerAccordionSelector = StatisticsHandler.Options.selectors.productListContainer + ' ' + StatisticsHandler.Options.selectors.productListAccordionContainer;
            let results = document.querySelector(productListContainerAccordionSelector);
            if (results instanceof HTMLElement) {
                results.innerHTML = '';
                results.style.height = '0';
                results.style.marginBottom = '0';
            }
            if (this.page || this.searchParams) {
                const url = new URL(endpoint);
                if (!url) {
                    return;
                }
                if (this.searchParams) {
                    this.searchParams.forEach(param => {
                        url.searchParams.append(param[0], param[1]);
                    });
                }
                if (this.hasAnySearchParamButPageChanged(url)) {
                    this.page = 1;// reset the page to show correct results
                }
                if (this.page && this.page > 0) {
                    url.searchParams.set('page', this.page?.toString() ?? '');
                }
                endpoint = url.toString();
            }
        }

        try {
            this.lastUsedUrl = new URL(endpoint);
            const response = await fetch(endpoint);
            const data = await response.json();
            if (data.success) {
                this.renderResponse(data);
                const loadingElem = this.resultContainer.querySelector(StatisticsHandler.Options.selectors.loading) as HTMLElement;
                if (loadingElem) {
                    loadingElem.style.height = '0';
                }
                initAccordionComponents();
                this.catchPaginatorClicks();
                this.catchFormSubmit();
                this.catchFormReset();
            } else {
                console.error('Failed to fetch statistics:', data.message);
                this.renderResponse(data);
            }
        } catch (error) {
            console.error('Failed to fetch statistics:', error);
        } finally {
            loading?.destroy();
        }
    }

    private hasAnySearchParamButPageChanged(newUrl: URL) {
        if (!this.lastUsedUrl) return false;
        const newParams = new URLSearchParams(newUrl.search);
        const lastParams = new URLSearchParams(this.lastUsedUrl.search);
        newParams.delete('page');
        lastParams.delete('page')
        return newParams.toString() !== lastParams.toString();
    }

    private renderResponse(data: { html: any; }) {
        const html = data.html;
        if (!html.length) {
            throw new Error('No statistics found');
        }
        // @ts-ignore
        this.resultContainer.innerHTML = html;
    }

    private initLoading(selector: string): { destroy: () => void } | null {
        const loadingElem = document.querySelector(selector) as HTMLElement;
        if (!loadingElem) return null;
        loadingElem.style.height = '100px';
        setTimeout(() => {
            this.resultContainer?.scrollIntoView();
        }, 20)

        const loading = new Loading(loadingElem, 'triple', false);
        return {
            destroy: () => {
                loading.destroy();
                loadingElem.style.height = '0';
            }
        };
    }

    private catchPaginatorClicks() {
        const productListPaginatorSelector = StatisticsHandler.Options.selectors.resultContainer + ' ' + StatisticsHandler.Options.selectors.productListContainer + ' ' + StatisticsHandler.Options.selectors.paginationLinks;
        const items = document.querySelectorAll(productListPaginatorSelector);
        items.forEach(item => {
            if (!(item instanceof HTMLAnchorElement)) return;
            item.addEventListener('click', (e) => {
                e.preventDefault();
                const href = item.href;
                if (!href) return;
                const page = new URL(href)?.searchParams.get('page');
                if (!page) return;
                this.page = parseInt(page);
                this.updateStatistics();
            });
        })
    }

    private catchFormSubmit() {
        const form = document.querySelector(StatisticsHandler.Options.selectors.resultContainer + ' ' + StatisticsHandler.Options.selectors.form);
        if (!(form instanceof HTMLFormElement)) return;
        form.addEventListener('submit', (e) => {
            e.preventDefault();
            this.searchParams = Array.from(new FormData(form)).filter((filter) => StatisticsHandler.Options.supportedFilters.includes(filter[0]));
            this.updateStatistics();
        });
    }

    private catchFormReset() {
        const formResetSelector = StatisticsHandler.Options.selectors.resultContainer + ' ' + StatisticsHandler.Options.selectors.form + ' ' + StatisticsHandler.Options.selectors.formResetLink;
        const formReset = document.querySelector(formResetSelector);
        if (!(formReset instanceof HTMLAnchorElement)) return;
        formReset.addEventListener('click', (e) => {
            e.preventDefault();
            this.searchParams = undefined;
            this.page = undefined;
            this.updateStatistics();
        });
    }
}

export {StatisticsHandler};
